import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { CustomizationProvider } from './contexts/CustomizationContext';
import { BecomeGuestProvider } from './contexts/BecomeGuestContext';
import { useAuth } from './hooks/useAuth';
import 'antd/dist/reset.css';
import './styles/theme.css';

// settings (consolidated)
import {
    UserManagement,
    SystemSettings,
    Customization,
    Profile,
    Contacts,
    UserSettings,
    Settings,
} from './components/settings';

// auth
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import BecomeGuest from './components/auth/BecomeGuest';
import RegistrationSuccess from './components/auth/RegistrationSuccess';
import SetPassword from './components/auth/SetPassword';
import ForgotPassword from './components/auth/ForgotPassword';

// index
import HomePage from './components/index/HomePage';

// layout
import Layout from './components/layout/Layout';
import SideMenuLayout from './components/layout/SideMenuLayout';

// messages
import MessageCompose from './components/messages/MessageCompose';
import MessageReply from './components/messages/MessageReply';
import MessageView from './components/messages/MessageView';
import MessagePreview from './components/messages/MessagePreview';

// documents
import DocumentCompose from './components/documents/DocumentCompose';
import DocumentView from './components/documents/DocumentView';
import DocumentPreview from './components/documents/DocumentPreview';

// documenso (signature requests)
import DocumensoCompose from './components/documenso/DocumensoCompose';
import DocumensoView from './components/documenso/DocumensoView';
import DocumensoPreview from './components/documenso/DocumensoPreview';

// profile
import Mailbox from './components/profile/Mailbox';
import ContactsMailbox from './components/profile/ContactsMailbox';

import LinkViewRoute from './components/routes/LinkViewRoute';
import ContactsMailboxRoute from './components/routes/ContactsMailboxRoute';

// Private route wrapper - redirects to login if not authenticated
const PrivateRoute = ({ children }) => {
    const { isLoggedIn } = useAuth();
    return isLoggedIn ? children : <Navigate to="/login" replace />;
};

// Public route wrapper - redirects to mailbox if already logged in
const PublicRoute = ({ children }) => {
    const { isLoggedIn } = useAuth();
    return isLoggedIn ? <Navigate to="/profile/mailbox" replace /> : children;
};

// Private route with SideMenuLayout
const PrivateSideMenuRoute = ({ children }) => {
    const { isLoggedIn } = useAuth();
    
    if (!isLoggedIn) {
        return <Navigate to="/login" replace />;
    }
    
    return <SideMenuLayout>{children}</SideMenuLayout>;
};

// Admin-only route - redirects non-admins to settings
const AdminSettingsRoute = ({ children }) => {
    const { isLoggedIn, isAdmin } = useAuth();
    if (!isLoggedIn) return <Navigate to="/login" replace />;
    if (!isAdmin) return <Navigate to="/settings#prefs-security" replace />;
    return children;
};


function App() {
    return (
        <AuthProvider>
            <CustomizationProvider>
                <BecomeGuestProvider>
                <Router>
                    <Routes>
                        {/* ===== PUBLIC ROUTES (with Layout header) ===== */}
                        <Route path="/" element={<Layout><HomePage /></Layout>} />
                        <Route path="/mailbox" element={<ContactsMailboxRoute />} />
                        <Route path="/login" element={<Layout><PublicRoute><Login /></PublicRoute></Layout>} />
                        <Route path="/register" element={<Layout><PublicRoute><Register /></PublicRoute></Layout>} />
                        <Route path="/become-guest" element={<Layout><BecomeGuest /></Layout>} />
                        <Route path="/registration-success" element={<Layout><RegistrationSuccess /></Layout>} />
                        <Route path="/set-password/:uniqueIdentifier" element={<Layout><SetPassword /></Layout>} />
                        <Route path="/forgot-password" element={<Layout><PrivateRoute><ForgotPassword /></PrivateRoute></Layout>} />
                        
                        {/* ===== PUBLIC MESSAGE/DOCUMENT VIEWS - no auth required; SideMenuLayout when logged in ===== */}
                        <Route path="/link/preview/:unique_identifier" element={<LinkViewRoute><MessagePreview /></LinkViewRoute>} />
                        <Route path="/document/preview/:unique_identifier" element={<LinkViewRoute><DocumentPreview /></LinkViewRoute>} />
                        <Route path="/documenso/link/preview/:unique_identifier" element={<LinkViewRoute><DocumensoPreview /></LinkViewRoute>} />
                        <Route path="/link/reply/:unique_identifier" element={<LinkViewRoute><MessageReply /></LinkViewRoute>} />
                        <Route path="/documenso/link/reply/:unique_identifier" element={<LinkViewRoute><DocumensoView /></LinkViewRoute>} />
                        <Route path="/link/:unique_identifier" element={<LinkViewRoute><MessageView /></LinkViewRoute>} />
                        <Route path="/document/:unique_identifier" element={<LinkViewRoute><DocumentView /></LinkViewRoute>} />
                        <Route path="/documenso/link/:unique_identifier" element={<LinkViewRoute><DocumensoView /></LinkViewRoute>} />

                        {/* ===== PRIVATE ROUTES (with SideMenuLayout) ===== */}
                        <Route path="/profile/mailbox" element={<PrivateSideMenuRoute><Mailbox /></PrivateSideMenuRoute>} />
                        <Route path="/profile/contacts" element={<PrivateSideMenuRoute><Contacts /></PrivateSideMenuRoute>} />
                        <Route path="/profile" element={<PrivateSideMenuRoute><Profile /></PrivateSideMenuRoute>} />
                        <Route path="/profile/user-settings" element={<Navigate to="/settings#prefs-security" replace />} />
                        <Route path="/message-compose" element={<PrivateSideMenuRoute><MessageCompose /></PrivateSideMenuRoute>} />
                        <Route path="/document-compose" element={<PrivateSideMenuRoute><DocumentCompose /></PrivateSideMenuRoute>} />
                        <Route path="/signature-request" element={<PrivateSideMenuRoute><DocumensoCompose /></PrivateSideMenuRoute>} />
                        
                        {/* ===== SETTINGS (unified single-page) ===== */}
                        <Route path="/settings/*" element={<PrivateSideMenuRoute><Settings /></PrivateSideMenuRoute>} />

                        {/* ===== ADMIN ROUTES (redirect to Settings hash) ===== */}
                        <Route path="/admin/user-management" element={<Navigate to="/settings#user-management" replace />} />
                        <Route path="/admin/system-settings" element={<Navigate to="/settings#system-settings-general" replace />} />
                        <Route path="/admin/customization" element={<Navigate to="/settings#customization-branding" replace />} />
                    </Routes>
                </Router>
                </BecomeGuestProvider>
            </CustomizationProvider>
        </AuthProvider>
    );
}

export default App;
